import { onboardingAgreementOptions } from '@doneverse/data/queries'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ExternalLink, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import styles from './AgreementSigning.module.css'
import { OnboardingLockCard } from './OnboardingLockCard'
import { Button } from '@/components/ui/button'
import { EmbedLoader } from '@/components/ui/embed-loader'
import { useProfilePoll } from '@/hooks/useProfilePoll'

/**
 * Onboarding step 1's signing surface — ClickUp `86d3uknup` (Onboarding 03).
 *
 * The founder's service agreement, embedded. It is the step's whole screen
 * because at step 1 there is no dashboard behind it yet.
 *
 * What this surface CANNOT do is finish the step, and everything about its
 * states follows from that. Signing in the frame applies a Keap tag; Keap's
 * `founder.agreement_signed` event is the only thing that closes step 1
 * (decision #1). So the founder signs, this surface has no idea, and the step
 * closes moments later when the event lands — at which point the tracker
 * unmounts this and moves to step 2. There is no "done" button here to press,
 * and adding one would be a lie about who owns the state.
 *
 * Three states, in the order a founder meets them:
 *
 *   embed    — the document, in an iframe.
 *   fallback — the embed did not load (blocked frames, a dead network, an
 *              unconfigured DocuSeal). Offers the same document in a new tab,
 *              which is a real way through rather than an apology.
 *   waiting  — DocuSeal already has the signature. Re-presenting a signed
 *              document would invite a founder to sign it twice and wonder why
 *              nothing happened, so this says what is actually happening.
 */

/**
 * How long the frame gets before we assume it will never paint.
 *
 * Cross-origin means `onLoad` is the only signal available — there is no error
 * event for "the browser refused to frame this", and no way to see inside. A
 * timeout is therefore the only detector of the failure mode the ticket names,
 * and it is generous: a slow document that paints at 11s cancels the fallback
 * on load, so the cost of being wrong is a link the founder ignores.
 */
const EMBED_TIMEOUT_MS = 12_000

/**
 * How often to re-read `/v1/me` while the founder is on this screen — see
 * {@link useProfilePoll} for why a poll exists at all. Slower than the booking
 * surface's: signing a contract takes minutes, not the seconds a Calendly
 * confirmation does, and there is no give-up state waiting on the answer.
 */
const STEP_POLL_MS = 15_000

export function AgreementSigning() {
  const { data: agreement, isPending, isError, refetch } = useQuery(onboardingAgreementOptions())
  const [embed, setEmbed] = useState<'pending' | 'loaded' | 'blocked'>('pending')

  useProfilePoll(STEP_POLL_MS)

  const embedSrc = agreement?.embedSrc
  useEffect(() => {
    if (!embedSrc || embed !== 'pending') return
    const timer = setTimeout(() => setEmbed('blocked'), EMBED_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [embedSrc, embed])

  if (isPending) {
    return (
      <Frame title="Sign your agreement" description="Opening your agreement…">
        <Waiting />
      </Frame>
    )
  }

  // `null` is a 503 — DocuSeal is not wired up here, and the founder is being
  // sent the agreement by email instead. `isError` is everything else. Both
  // land on the same screen because the founder's options are identical: their
  // agreement is in their inbox, and the step still closes when they sign it.
  if (isError || !agreement) {
    return (
      <Frame
        title="Your agreement is on its way"
        description="We've sent it to your email. Sign it there and this step will tick itself off — no need to stay on this screen."
      >
        <Waiting />
        {isError && (
          <Button variant="outline" size="sm" onClick={() => void refetch()} data-testid="agreement-retry">
            Try again
          </Button>
        )}
      </Frame>
    )
  }

  if (agreement.signed) {
    return (
      <Frame
        title="Thanks — that's signed"
        description="We're confirming it now. This moves on by itself the moment it lands, so you can leave this open or come back later."
      >
        <Waiting />
      </Frame>
    )
  }

  if (embed === 'blocked') {
    return (
      <Frame
        title="The document won't open here"
        description="Your browser is blocking it. Open it in a new tab instead — signing there works exactly the same, and this page will catch up on its own."
      >
        <div className={styles.fallback} data-testid="agreement-fallback">
          <AlertTriangle className={styles.fallbackIcon} aria-hidden />
          <Button asChild size="lg">
            <a href={agreement.hostedUrl} target="_blank" rel="noopener noreferrer">
              Open your agreement
              <ExternalLink className={styles.linkIcon} aria-hidden />
            </a>
          </Button>
        </div>
      </Frame>
    )
  }

  return (
    <Frame
      wide
      title="Sign your agreement"
      description="Review and sign your Doneverse agreement. This step moves on by itself once your signature is confirmed."
    >
      <EmbedLoader className={styles.embed} loading={embed !== 'loaded'} label="Loading your agreement…">
        <iframe
          src={agreement.embedSrc}
          title="Your Doneverse service agreement"
          className={styles.frame}
          onLoad={() => setEmbed('loaded')}
          data-testid="agreement-embed"
        />
      </EmbedLoader>
      {/* Always available, not only after the timeout fires: a founder on a
          phone may simply prefer the full-page document, and a frame that
          renders but scrolls badly is a failure the timeout never sees. */}
      <a
        className={styles.newTab}
        href={agreement.hostedUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="agreement-hosted-link"
      >
        Trouble signing here? Open it in a new tab
        <ExternalLink className={styles.linkIcon} aria-hidden />
      </a>
    </Frame>
  )
}

/**
 * The surface, which is the shared onboarding lock at reading width.
 *
 * This REPLACES the tracker's overlay rather than layering over it, so it must
 * carry the same guarantees that overlay did — that is exactly what rendering
 * through `OnboardingLockCard` buys, rather than a second hand-written copy of
 * them. `wide` is the whole difference: a contract in the 34rem message card
 * would put a signature field in a letterbox.
 *
 * `announceOnly` follows from `wide`. In the signing state the document is the
 * content, so the title and description are the dialog's accessible name and
 * nothing more; in the message states they ARE the content and are drawn.
 */
function Frame({
  wide,
  title,
  description,
  children,
}: {
  wide?: boolean
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <OnboardingLockCard
      testId="agreement-surface"
      wide={wide}
      announceOnly={wide}
      title={title}
      description={description}
    >
      {children}
    </OnboardingLockCard>
  )
}

/** The spinner beside a waiting title the frame has already drawn. */
function Waiting() {
  return (
    <div className={styles.waiting} data-testid="agreement-waiting">
      <Loader2 className={`${styles.spinner} animate-spin`} aria-hidden />
    </div>
  )
}
